import type { FC } from 'react'
import { useEffect, useState } from 'react'
import {
  autoUpdate,
  flip,
  offset,
  safePolygon,
  shift,
  useFloating,
  useHover,
  useInteractions,
} from '@floating-ui/react'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Paper from '@mui/material/Paper'
import Portal from '@mui/material/Portal'
import Typography from '@mui/material/Typography'
import ChevronRight from '@mui/icons-material/ChevronRight'
import LanguageIcon from '@mui/icons-material/Language'

import { LanguageMenuBody } from '@/layouts/dashboard/language-switch/language-popover'

import {
  accountPopoverMenuRowSx,
  accountSubpopoverMainAxisGapPx,
} from './account-menu-row-sx'
import { densityPx } from '@/theme/base/density'

/**
 * Ligne « Langue » avec sous-menu au survol (même pattern que AddFilterPopover :
 * Floating UI + safePolygon pour éviter le clignotement entre référence et panneau).
 */
export const AccountLanguageHoverRow: FC<{
  parentOpen: boolean
  label: string
}> = ({ parentOpen, label }) => {
  const [isOpen, setIsOpen] = useState(false)

  useEffect(() => {
    if (!parentOpen) {
      setIsOpen(false)
    }
  }, [parentOpen])

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement: 'right-start',
    middleware: [offset(accountSubpopoverMainAxisGapPx), flip(), shift()],
    whileElementsMounted: autoUpdate,
  })

  const hover = useHover(context, {
    handleClose: safePolygon({ blockPointerEvents: true }),
    delay: { open: 0, close: 0 },
  })

  const { getReferenceProps, getFloatingProps } = useInteractions([hover])

  return (
    <>
      <ListItemButton
        ref={refs.setReference}
        data-testid="account-popover-language"
        sx={{
          ...accountPopoverMenuRowSx,
          bgcolor: isOpen ? 'action.hover' : 'transparent',
        }}
        {...getReferenceProps()}
      >
        <ListItemIcon>
          <LanguageIcon />
        </ListItemIcon>
        <ListItemText
          sx={{ flex: '1 1 auto', minWidth: 0, my: 0 }}
          primary={<Typography variant="body2">{label}</Typography>}
        />
        <ChevronRight
          fontSize="small"
          sx={{ color: 'text.secondary', flexShrink: 0 }}
        />
      </ListItemButton>
      {isOpen ? (
        <Portal>
          <Paper
            ref={refs.setFloating}
            elevation={3}
            style={{ ...floatingStyles, zIndex: 1400 }}
            sx={{
              borderRadius: 1.5,
              border: 1,
              borderStyle: 'solid',
              borderColor: 'divider',
              width: densityPx(220),
              py: 0.5,
              overflow: 'visible',
            }}
            {...getFloatingProps()}
          >
            <LanguageMenuBody />
          </Paper>
        </Portal>
      ) : null}
    </>
  )
}
